/**
 * RACKET QUIZ — UI CONTROLLER
 * Manages quiz navigation, state, result fetching, and display.
 * Depends on quiz-scoring.js (QuizScoring global).
 */

(function () {
  'use strict';

  // ── State ──────────────────────────────────────────────────────────────────

  var SCREENS = ['intro', 'q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8', 'loading', 'results'];
  var QUESTION_SCREENS = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8'];
  var currentScreen = 'intro';
  var answers = { style: null, level: null, improvement: null, budget: null, position: null, weakness: null, physique: null, comfort: null };
  var products = [];
  var locale = 'en';
  var advanceTimeout = null;

  // ── Answer Label Maps (for profile tags) ──────────────────────────────────

  var ANSWER_LABELS = {
    en: {
      style:       { power: 'Power', control: 'Control', hybrid: 'All-Round' },
      level:       { Beginner: 'Beginner', Intermediate: 'Intermediate', Advanced: 'Advanced', Professional: 'Pro' },
      improvement: { more_power: 'More Power', sweet_spot: 'Sweet Spot', more_control: 'Control', balance: 'Balance' },
      budget:      { budget: 'Value', mid: 'Mid-Range', premium: 'Premium', elite: 'Elite' },
      position:    { right_right: 'Right / Right', right_left: 'Right / Left', left_left: 'Left / Left', left_right: 'Left / Right' },
      weakness:    { glass_too_much: 'Glass Control', weak_smash: 'Smash Power', mishits: 'Sweet Spot', defense_weak: 'Defense' },
      physique:    { athletic: 'Athletic', average: 'Average', slim: 'Slim' },
      comfort:     { no_issues: 'No Issues', slight_discomfort: 'Some Discomfort', arm_issues: 'Arm Issues' }
    },
    ar: {
      style:       { power: 'القوة', control: 'التحكم', hybrid: 'متوازن' },
      level:       { Beginner: 'مبتدئ', Intermediate: 'متوسط', Advanced: 'متقدم', Professional: 'محترف' },
      improvement: { more_power: 'قوة أكتر', sweet_spot: 'سويت سبوت', more_control: 'تحكم', balance: 'توازن' },
      budget:      { budget: 'اقتصادي', mid: 'متوسط', premium: 'مميز', elite: 'بلا حدود' },
      position:    { right_right: 'يمين / يمين', right_left: 'يمين / شمال', left_left: 'شمال / شمال', left_right: 'شمال / يمين' },
      weakness:    { glass_too_much: 'تحكم بالزجاج', weak_smash: 'قوة السماش', mishits: 'سويت سبوت', defense_weak: 'دفاع' },
      physique:    { athletic: 'رياضي', average: 'متوسط', slim: 'نحيف' },
      comfort:     { no_issues: 'بدون مشاكل', slight_discomfort: 'انزعاج بسيط', arm_issues: 'مشاكل في الإيد' }
    }
  };

  var ANSWER_ICONS = {
    style: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>',
    level: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>',
    improvement: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>',
    budget: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>',
    position: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 11V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2"/><path d="M14 10V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2"/><path d="M10 10.5V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2v8"/><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 13"/></svg>',
    weakness: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22c.3-.2 7-3.5 7-10V5l-7-3-7 3v7c0 6.5 6.7 9.8 7 10z"/></svg>',
    physique: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>',
    comfort: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>'
  };

  // ── DOM References ─────────────────────────────────────────────────────────

  var quizPage, progressBar, progressFill, progressText, resultsGrid, summaryEl,
      tipEl, tipTextEl, emptyEl, heroEl, heroCardEl, heroCircleEl, heroPctEl,
      heroWordEl, heroReasonsEl, heroLabelEl, profileEl, profileTitleEl,
      profileTagsEl, alsoEl, alsoTitleEl;

  function init() {
    quizPage = document.querySelector('.quiz-page');
    if (!quizPage) return;

    // Detect locale
    locale = quizPage.dataset.quizLocale || document.documentElement.lang || 'en';
    if (locale.startsWith('ar')) locale = 'ar';

    // Cache DOM
    progressBar  = quizPage.querySelector('[data-quiz-progress]');
    progressFill = quizPage.querySelector('[data-quiz-progress-fill]');
    progressText = quizPage.querySelector('[data-quiz-progress-text]');
    resultsGrid  = quizPage.querySelector('[data-quiz-results-grid]');
    summaryEl    = quizPage.querySelector('[data-quiz-summary]');
    tipEl        = quizPage.querySelector('[data-quiz-tip]');
    tipTextEl    = quizPage.querySelector('[data-quiz-tip-text]');
    emptyEl      = quizPage.querySelector('[data-quiz-empty]');

    // New hero elements
    heroEl        = quizPage.querySelector('[data-quiz-hero]');
    heroCardEl    = quizPage.querySelector('[data-quiz-hero-card]');
    heroCircleEl  = quizPage.querySelector('[data-quiz-hero-circle]');
    heroPctEl     = quizPage.querySelector('[data-quiz-hero-pct]');
    heroWordEl    = quizPage.querySelector('[data-quiz-hero-word]');
    heroReasonsEl = quizPage.querySelector('[data-quiz-hero-reasons]');
    heroLabelEl   = quizPage.querySelector('[data-quiz-hero-label]');

    // Profile elements
    profileEl      = quizPage.querySelector('[data-quiz-profile]');
    profileTitleEl = quizPage.querySelector('[data-quiz-profile-title]');
    profileTagsEl  = quizPage.querySelector('[data-quiz-profile-tags]');

    // Also great section
    alsoEl      = quizPage.querySelector('[data-quiz-also]');
    alsoTitleEl = quizPage.querySelector('[data-quiz-also-title]');

    // Load product data
    loadProducts();

    // Bind events
    bindEvents();
  }

  function loadProducts() {
    var el = document.querySelector('[data-quiz-products]');
    if (!el) return;
    try {
      products = JSON.parse(el.textContent);
    } catch (e) {
      products = [];
    }
  }

  // ── Event Binding ──────────────────────────────────────────────────────────

  function bindEvents() {
    // Start button
    var startBtn = quizPage.querySelector('[data-quiz-start]');
    if (startBtn) {
      startBtn.addEventListener('click', function () {
        showScreen('q1');
      });
    }

    // Option clicks (delegated)
    quizPage.addEventListener('click', function (e) {
      var option = e.target.closest('.quiz-option');
      if (!option) return;

      var screen = option.closest('[data-quiz-screen]');
      if (!screen) return;

      handleOptionSelect(screen, option);
    });

    // Back buttons (delegated)
    quizPage.addEventListener('click', function (e) {
      var backBtn = e.target.closest('[data-quiz-back]');
      if (!backBtn) return;
      goBack();
    });

    // Retake button
    var retakeBtn = quizPage.querySelector('[data-quiz-retake]');
    if (retakeBtn) {
      retakeBtn.addEventListener('click', function () {
        retakeQuiz();
      });
    }
  }

  // ── Screen Navigation ──────────────────────────────────────────────────────

  function showScreen(name) {
    if (advanceTimeout) {
      clearTimeout(advanceTimeout);
      advanceTimeout = null;
    }

    currentScreen = name;

    // Toggle screens
    var screens = quizPage.querySelectorAll('[data-quiz-screen]');
    screens.forEach(function (el) {
      var isTarget = el.dataset.quizScreen === name;
      el.classList.toggle('is-active', isTarget);
    });

    // Show/hide progress bar
    var isQuestion = QUESTION_SCREENS.indexOf(name) !== -1;
    if (progressBar) {
      progressBar.style.display = isQuestion ? '' : 'none';
    }

    // Update progress
    if (isQuestion) {
      updateProgress();
    }

    // Scroll to top of quiz
    quizPage.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // Move focus to the new screen's heading for keyboard users
    var activeScreen = quizPage.querySelector('[data-quiz-screen].is-active');
    if (activeScreen) {
      var heading = activeScreen.querySelector('h1, h2, h3, h4');
      if (heading) {
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        setTimeout(function () { heading.focus(); }, 50);
      }
    }
  }

  function goBack() {
    var idx = SCREENS.indexOf(currentScreen);
    if (idx > 1) {
      var prevScreen = SCREENS[idx - 1];
      if (prevScreen === 'intro') return;
      showScreen(prevScreen);
    } else if (idx === 1) {
      showScreen('intro');
    }
  }

  function goNext() {
    var idx = SCREENS.indexOf(currentScreen);
    var nextScreen = SCREENS[idx + 1];
    if (nextScreen === 'loading') {
      showScreen('loading');
      runScoringAndFetch();
    } else if (nextScreen) {
      showScreen(nextScreen);
    }
  }

  // ── Progress Bar ───────────────────────────────────────────────────────────

  function updateProgress() {
    var step = QUESTION_SCREENS.indexOf(currentScreen) + 1;
    var total = QUESTION_SCREENS.length;
    var pct = (step / total) * 100;

    if (progressFill) {
      progressFill.style.width = pct + '%';
    }
    if (progressText) {
      var tpl = locale === 'ar'
        ? 'السؤال {current} من {total}'
        : 'Question {current} of {total}';
      progressText.textContent = tpl.replace('{current}', step).replace('{total}', total);
    }
  }

  // ── Option Selection ───────────────────────────────────────────────────────

  function handleOptionSelect(screen, option) {
    // Clear previous selection
    var allOptions = screen.querySelectorAll('.quiz-option');
    allOptions.forEach(function (o) { o.classList.remove('is-selected'); });

    // Mark selected
    option.classList.add('is-selected');

    // Store answer
    var key = screen.dataset.quizKey;
    var value = option.dataset.value;
    if (key) answers[key] = value;

    // Auto-advance after delay
    advanceTimeout = setTimeout(function () {
      goNext();
    }, 400);
  }

  // ── Circle Animation ──────────────────────────────────────────────────────

  var CIRCLE_CIRCUMFERENCE = 326.73; // 2 * PI * 52

  function animateCircle(circleEl, pctEl, targetPct, delay) {
    delay = delay || 0;
    var fillEl = circleEl.querySelector('[data-quiz-circle-fill]');
    if (!fillEl || !pctEl) return;

    // Reset
    fillEl.style.strokeDashoffset = CIRCLE_CIRCUMFERENCE;
    pctEl.textContent = '0';

    setTimeout(function () {
      // Animate the SVG circle
      var targetOffset = CIRCLE_CIRCUMFERENCE - (CIRCLE_CIRCUMFERENCE * targetPct / 100);
      fillEl.style.transition = 'stroke-dashoffset 1.2s ease-out';
      fillEl.style.strokeDashoffset = targetOffset;

      // Animate the number
      var start = 0;
      var duration = 1200;
      var startTime = null;

      function step(ts) {
        if (!startTime) startTime = ts;
        var progress = Math.min((ts - startTime) / duration, 1);
        var eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
        var current = Math.round(start + (targetPct - start) * eased);
        pctEl.textContent = current;
        if (progress < 1) {
          requestAnimationFrame(step);
        }
      }
      requestAnimationFrame(step);
    }, delay);
  }

  // ── Scoring & Result Fetching ──────────────────────────────────────────────

  function runScoringAndFetch() {
    if (!products.length) {
      showScreen('results');
      showEmpty();
      return;
    }

    // Run scoring
    var ranked = QuizScoring.rankProducts(products, answers, 6);

    // Fetch result cards in parallel
    var sectionId = 'quiz-result-card';
    var fetches = ranked.map(function (result) {
      return fetch('/products/' + result.product.handle + '?section_id=' + sectionId)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          return { html: html, result: result };
        })
        .catch(function () {
          return { html: null, result: result };
        });
    });

    // Wait for loading animation (min 1.5s) + all fetches
    var loadingMin = new Promise(function (resolve) {
      setTimeout(resolve, 1500);
    });

    Promise.all([Promise.all(fetches), loadingMin]).then(function (values) {
      var fetchResults = values[0];
      displayResults(fetchResults, ranked);
    });
  }

  // ── Profile Rendering ─────────────────────────────────────────────────────

  function renderProfile() {
    var isAr = locale === 'ar';

    // Profile title
    if (profileTitleEl) {
      profileTitleEl.textContent = isAr ? 'ملف لاعبك' : 'Your Player Profile';
    }

    // Summary
    if (summaryEl) {
      summaryEl.textContent = QuizScoring.generateSummary(answers, locale);
    }

    // Profile tags
    if (profileTagsEl) {
      profileTagsEl.innerHTML = '';
      var labels = ANSWER_LABELS[isAr ? 'ar' : 'en'];
      var keys = ['style', 'level', 'improvement', 'budget', 'position', 'weakness', 'physique', 'comfort'];

      keys.forEach(function (key) {
        if (!answers[key]) return;
        var label = (labels[key] || {})[answers[key]] || answers[key];
        var tag = document.createElement('span');
        tag.className = 'quiz-profile__tag';
        tag.innerHTML = ANSWER_ICONS[key] + ' ' + label;
        profileTagsEl.appendChild(tag);
      });
    }

    // Smart tip
    var tip = QuizScoring.generateSmartTip(answers, locale);
    if (tip && tipEl && tipTextEl) {
      tipTextEl.textContent = tip;
      tipEl.style.display = '';
    } else if (tipEl) {
      tipEl.style.display = 'none';
    }
  }

  // ── Display Results ───────────────────────────────────────────────────────

  function displayResults(fetchResults, ranked) {
    showScreen('results');

    // Render profile section
    renderProfile();

    if (!fetchResults.length || !fetchResults[0].html) {
      showEmpty();
      return;
    }

    // ── Hero: Top pick (#1) ──
    var topItem = fetchResults[0];
    if (topItem.html && heroEl) {
      var heroHtml = stripSectionWrapper(topItem.html);
      if (heroHtml && heroCardEl) {
        var wrapper = document.createElement('div');
        wrapper.innerHTML = heroHtml;
        var card = wrapper.querySelector('.quiz-result-card');
        if (!card) card = wrapper.firstElementChild;

        if (card) {
          // Remove the badge from hero card (we show percentage in the circle instead)
          var cardBadge = card.querySelector('[data-quiz-badge]');
          if (cardBadge) cardBadge.style.display = 'none';

          // Remove explanation from card (we show it in hero reasons)
          var cardExpl = card.querySelector('[data-quiz-explanation]');
          if (cardExpl) cardExpl.style.display = 'none';

          heroCardEl.innerHTML = '';
          heroCardEl.appendChild(card);

          // Hero label
          var isAr = locale === 'ar';
          if (heroLabelEl) {
            heroLabelEl.textContent = isAr ? 'أفضل اختيار لك' : 'Your Best Match';
          }

          // Hero circle animation
          var badge = QuizScoring.getBadge(topItem.result.displayPercentage, locale);
          if (heroWordEl) {
            heroWordEl.textContent = badge.label;
          }

          // Set circle color
          var heroFill = heroEl.querySelector('[data-quiz-circle-fill]');
          if (heroFill) {
            heroFill.style.stroke = badge.color;
          }

          if (heroCircleEl && heroPctEl) {
            animateCircle(heroCircleEl, heroPctEl, topItem.result.displayPercentage, 300);
          }

          // Hero reasons (explanation broken into bullet points)
          if (heroReasonsEl) {
            var explanation = QuizScoring.generateExplanation(topItem.result, answers, locale);
            var sentences = explanation.split(/\.\s+|\.$/g).filter(function (s) { return s.trim(); });
            heroReasonsEl.innerHTML = '';
            sentences.forEach(function (sentence) {
              var li = document.createElement('div');
              li.className = 'quiz-hero__reason';
              li.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg><span>' + sentence.trim() + '.</span>';
              heroReasonsEl.appendChild(li);
            });
          }

          heroEl.style.display = '';
        }
      }
    }

    // ── Remaining cards (2–6) ──
    var remainingItems = fetchResults.slice(1);

    if (remainingItems.length > 0 && resultsGrid && alsoEl) {
      var isAr = locale === 'ar';
      if (alsoTitleEl) {
        alsoTitleEl.textContent = isAr ? 'خيارات ممتازة كمان' : 'Also Great For You';
      }

      resultsGrid.innerHTML = '';
      var hasCards = false;

      remainingItems.forEach(function (item, idx) {
        if (!item.html) return;

        var cardHtml = stripSectionWrapper(item.html);
        if (!cardHtml) return;

        var wrapper = document.createElement('div');
        wrapper.innerHTML = cardHtml;

        var card = wrapper.querySelector('.quiz-result-card');
        if (!card) card = wrapper.firstElementChild;
        if (!card) return;

        // Set up the mini circle percentage
        var circleEl = card.querySelector('[data-quiz-card-circle]');
        var pctEl = card.querySelector('[data-quiz-card-pct]');
        var labelEl = card.querySelector('[data-quiz-badge-label]');
        var fillEl = card.querySelector('[data-quiz-circle-fill]');

        var badge = QuizScoring.getBadge(item.result.displayPercentage, locale);

        if (labelEl) {
          labelEl.textContent = badge.label;
        }
        if (fillEl) {
          fillEl.style.stroke = badge.color;
        }

        // Inject explanation
        var explanationEl = card.querySelector('[data-quiz-explanation]');
        if (explanationEl) {
          var explanation = QuizScoring.generateExplanation(item.result, answers, locale);
          explanationEl.textContent = explanation;
        }

        resultsGrid.appendChild(card);
        hasCards = true;

        // Animate circle with staggered delay
        if (circleEl && pctEl) {
          animateCircle(circleEl, pctEl, item.result.displayPercentage, 600 + (idx * 150));
        }
      });

      if (hasCards) {
        alsoEl.style.display = '';
      }
    }

    // Show empty state if nothing rendered
    if (!heroEl || heroEl.style.display === 'none') {
      if (!resultsGrid || !resultsGrid.children.length) {
        showEmpty();
      }
    }
    if (emptyEl && (heroEl && heroEl.style.display !== 'none')) {
      emptyEl.style.display = 'none';
    }
  }

  function stripSectionWrapper(html) {
    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');

    var sectionWrapper = doc.querySelector('.shopify-section');
    if (sectionWrapper) {
      return sectionWrapper.innerHTML;
    }

    return doc.body ? doc.body.innerHTML : html;
  }

  function showEmpty() {
    if (emptyEl) emptyEl.style.display = '';
    if (resultsGrid) resultsGrid.innerHTML = '';
    if (heroEl) heroEl.style.display = 'none';
    if (alsoEl) alsoEl.style.display = 'none';
  }

  // ── Retake ─────────────────────────────────────────────────────────────────

  function retakeQuiz() {
    answers = { style: null, level: null, improvement: null, budget: null, position: null, weakness: null, physique: null, comfort: null };

    // Clear all selections
    var selected = quizPage.querySelectorAll('.quiz-option.is-selected');
    selected.forEach(function (o) { o.classList.remove('is-selected'); });

    // Clear results
    if (resultsGrid) resultsGrid.innerHTML = '';
    if (summaryEl) summaryEl.textContent = '';
    if (tipEl) tipEl.style.display = 'none';
    if (emptyEl) emptyEl.style.display = 'none';
    if (heroEl) heroEl.style.display = 'none';
    if (heroCardEl) heroCardEl.innerHTML = '';
    if (heroReasonsEl) heroReasonsEl.innerHTML = '';
    if (profileTagsEl) profileTagsEl.innerHTML = '';
    if (alsoEl) alsoEl.style.display = 'none';

    showScreen('intro');
  }

  // ── Init ───────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
